
      export default {
        mounted() {
          // Unsaved-changes tracking is server-driven: nearly everything on
          // this page persists instantly on change, so a client-side "any
          // input = dirty" heuristic mostly produced bogus leave-page
          // warnings. Only the debounced save timers (items/customer) hold
          // genuinely unsaved state, and the server flags exactly those
          // windows via "dirty_state" {kind, dirty} events.
          this.dirtyKinds = new Set()
          this.tabScrolls = {}
          this.currentStep = this.el.dataset.currentStep
          this.scrolled = false

          this.handleEvent("open_in_new_tab", ({url}) => {
            window.open(url, "_blank")
          })
          this.handleEvent("dirty_state", ({kind, dirty}) => {
            if (dirty) this.dirtyKinds.add(kind)
            else this.dirtyKinds.delete(kind)
          })
          this.handleEvent("open_discount_modal", () => {
            const modal = document.getElementById("discount-modal")
            if (modal) modal.showModal()
          })

          // Save scroll position before switching tabs
          this.el.addEventListener("click", (e) => {
            const tab = e.target.closest('[role="tab"][phx-click="switch_tab"]')
            if (tab) this.tabScrolls[this.currentStep] = window.scrollY
          }, true)

          // Toggle data-scrolled on root when sentinel leaves viewport.
          // Every LiveView patch re-morphs this root element against
          // server-rendered HTML, which never contains data-scrolled — the
          // attribute gets wiped on every update (add/select/qty-change,
          // tab switch, anything). Keep the value on the hook too so
          // updated() can restore it without waiting for the observer to
          // re-fire, which only happens once the sentinel crosses the
          // viewport boundary again.
          const setScrolled = (v) => {
            this.scrolled = v
            this.el.dataset.scrolled = v.toString()
          }
          // The compact row stands in for the FULL panel (the stats grid, or
          // the Overview's summary card) — so it may show only while that
          // panel is hidden under the sticky block. It used to follow a
          // sentinel at the very top of the page: on the way back up the
          // full panel slid out from under the sticky block long before
          // the sentinel came into view, and both were on screen at once
          // (two thumbnails, two sets of totals). Measure the panel itself.
          // Two thresholds, not one: showing/hiding the row changes the
          // sticky block's height, which moves the panel; a single line
          // would flip-flop around it.
          const HEADER_H = 64
          this._syncScrolled = () => {
            const panel =
              document.getElementById("stats-grid") ||
              document.getElementById("order-summary-card")
            if (!panel) return false
            const tabs = document.getElementById("sticky-tabs")
            const line = HEADER_H + (tabs ? tabs.offsetHeight : 0)
            const bottom = panel.getBoundingClientRect().bottom
            if (!this.scrolled && bottom < line) setScrolled(true)
            else if (this.scrolled && bottom > line + 24) setScrolled(false)
            return true
          }
          this._onScroll = () => {
            if (this._scrollRaf) return
            this._scrollRaf = requestAnimationFrame(() => {
              this._scrollRaf = null
              this._syncScrolled()
            })
          }
          window.addEventListener("scroll", this._onScroll, { passive: true })
          window.addEventListener("resize", this._onScroll, { passive: true })

          // Tabs without a full panel keep the old rule: scrolled once the
          // top of the page has left the viewport.
          const sentinel = document.getElementById("scroll-sentinel")
          if (sentinel) {
            this._io = new IntersectionObserver(
              ([entry]) => {
                if (!this._syncScrolled()) setScrolled(!entry.isIntersecting)
              },
              { threshold: 0, rootMargin: "-64px 0px 0px 0px" }
            )
            this._io.observe(sentinel)
          }

          // Track sticky tabs height → CSS var for the search sticky offset.
          // Use offsetHeight (border-box) so padding/border are included — contentRect
          // would leave a visible gap equal to the tabs wrapper's vertical padding.
          const tabsEl = document.getElementById("sticky-tabs")
          if (tabsEl) {
            const updateTabsH = () => {
              const h = tabsEl.offsetHeight
              if (h > 0) this.el.style.setProperty("--tabs-h", h + "px")
            }
            updateTabsH()
            if (typeof ResizeObserver !== "undefined") {
              this._ro = new ResizeObserver(updateTabsH)
              this._ro.observe(tabsEl)
            }
            window.addEventListener("resize", updateTabsH, { passive: true })
            this._updateTabsH = updateTabsH
          }

          this._beforeUnload = (e) => {
            if (this.dirtyKinds.size > 0) { e.preventDefault(); e.returnValue = "" }
          }
          window.addEventListener("beforeunload", this._beforeUnload)
        },
        updated() {
          this.el.dataset.scrolled = this.scrolled.toString()
          // A patch can swap the full panel (tab switch) or change its height.
          if (this._onScroll) this._onScroll()

          const newStep = this.el.dataset.currentStep
          if (newStep !== this.currentStep) {
            const saved = this.tabScrolls[newStep]
            window.scrollTo({ top: saved || 0, behavior: "instant" })
            this.currentStep = newStep
          }
        },
        destroyed() {
          window.removeEventListener("beforeunload", this._beforeUnload)
          if (this._io) this._io.disconnect()
          if (this._ro) this._ro.disconnect()
          if (this._updateTabsH) window.removeEventListener("resize", this._updateTabsH)
          if (this._onScroll) {
            window.removeEventListener("scroll", this._onScroll)
            window.removeEventListener("resize", this._onScroll)
          }
          if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf)
        }
      }
    